// TAPINTO® Editor Frontend - Dashboard Page
// Version: 1.0
// Date: 2025-10-01

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, MoreVertical, Edit, Eye, Trash2 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useEditorStore } from '../stores/editorStore';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Modal } from '../components/common/Modal';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Page, CreatePageRequest } from '../types';
import { formatRelativeTime } from '../utils/helpers';
import toast from 'react-hot-toast';

export function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { createPage } = useEditorStore();
  
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreatePageRequest>({
    title: '',
    slug: '',
    page_type: 'landing_page',
    structure: {
      id: 'root',
      type: 'div',
      props: {},
      children: [],
    },
  });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    setLoading(true);
    try {
      // This would normally call the API
      // For now, we'll use mock data
      const mockPages: Page[] = [
        {
          page_id: '1',
          account_id: user?.account_id || '',
          page_type: 'landing_page',
          title: 'Welcome Page',
          slug: 'welcome',
          status: 'published',
          structure: { id: 'root', type: 'div', props: {}, children: [] },
          created_by: user?.user_id || '',
          last_edited_by: user?.user_id || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          published_at: new Date().toISOString(),
          version: 1,
        },
        {
          page_id: '2',
          account_id: user?.account_id || '',
          page_type: 'me_profile',
          title: 'My Profile',
          slug: 'profile',
          status: 'draft',
          structure: { id: 'root', type: 'div', props: {}, children: [] },
          created_by: user?.user_id || '',
          last_edited_by: user?.user_id || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          version: 2,
        },
      ];
      setPages(mockPages);
    } catch (error) {
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = async () => {
    if (!createForm.title.trim()) {
      toast.error('Page title is required');
      return;
    }

    if (!createForm.slug.trim()) {
      toast.error('Page slug is required');
      return;
    }

    const success = await createPage(createForm);
    if (success) {
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        slug: '',
        page_type: 'landing_page',
        structure: { id: 'root', type: 'div', props: {}, children: [] },
      });
      loadPages();
    }
  };

  const handleEditPage = (pageId: string) => {
    navigate(`/editor/${pageId}`);
  };

  const handleDeletePage = async (pageId: string) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      // This would normally call the API
      setPages(prev => prev.filter(page => page.page_id !== pageId));
      toast.success('Page deleted successfully');
    }
  };

  const filteredPages = pages.filter(page => {
    const matchesSearch = page.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         page.slug.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || page.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-primary-500 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold">T</span>
              </div>
              <h1 className="text-xl font-semibold text-neutral-900">
                TAPINTO® Editor
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-neutral-600">
                Welcome, {user?.email}
              </span>
              <Button variant="outline" onClick={logout}>
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">Pages</h2>
            <p className="text-neutral-600">Manage your pages and content</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Page
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Search pages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-32"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Pages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPages.map((page) => (
            <div key={page.page_id} className="card hover:shadow-md transition-shadow">
              <div className="card-header">
                <div className="flex justify-between items-start">
                  <h3 className="font-semibold text-neutral-900 truncate">
                    {page.title}
                  </h3>
                  <div className="flex items-center space-x-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(page.status)}`}>
                      {page.status}
                    </span>
                    <button className="p-1 hover:bg-neutral-100 rounded">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-neutral-500">/{page.slug}</p>
              </div>
              
              <div className="card-content">
                <div className="text-sm text-neutral-600">
                  <p>Type: {page.page_type.replace('_', ' ')}</p>
                  <p>Version: {page.version}</p>
                  <p>Updated: {formatRelativeTime(page.updated_at)}</p>
                </div>
              </div>
              
              <div className="card-footer">
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditPage(page.page_id)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {page.status === 'published' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/preview/${page.slug}`, '_blank')}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeletePage(page.page_id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredPages.length === 0 && (
          <div className="text-center py-12">
            <div className="text-neutral-400 mb-4">
              <Plus className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 mb-2">
              No pages found
            </h3>
            <p className="text-neutral-600 mb-4">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first page'
              }
            </p>
            {(!searchQuery && statusFilter === 'all') && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Page
              </Button>
            )}
          </div>
        )}
      </main>

      {/* Create Page Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Page"
        size="md"
      >
        <div className="p-6 space-y-4">
          <Input
            label="Page Title"
            value={createForm.title}
            onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Enter page title"
            required
          />
          
          <Input
            label="Page Slug"
            value={createForm.slug}
            onChange={(e) => setCreateForm(prev => ({ ...prev, slug: e.target.value }))}
            placeholder="page-slug"
            required
          />
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Page Type
            </label>
            <select
              value={createForm.page_type}
              onChange={(e) => setCreateForm(prev => ({ ...prev, page_type: e.target.value as any }))}
              className="input"
            >
              <option value="landing_page">Landing Page</option>
              <option value="me_profile">Me Profile</option>
              <option value="directory_listing">Directory Listing</option>
            </select>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreatePage}>
              Create Page
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
